import { type MouseEvent, memo, type ReactNode, useCallback } from 'react';

import { Tooltip } from '@air/primitive-tooltip';
import { tailwindVariants, type VariantProps } from '@air/utils-tailwind';

export type SegmentedControlOption = {
  label: string | ReactNode;
  value: string;
  id: string;
  tooltip: string;
  disabled?: boolean;
  testId?: string;
};

export type SegmentedControlProps = {
  options: SegmentedControlOption[];
  selectedOption: SegmentedControlOption;
  onSelect: (option: SegmentedControlOption) => void;
  color?:
    | 'blue'
    | 'gray'
    | /** @deprecated Use 'gray' instead. British spelling kept for backwards compatibility (DES-25). */ 'grey'
    | 'violet';
  outerColor?:
    | 'gray'
    | /** @deprecated Use 'gray' instead. British spelling kept for backwards compatibility (DES-25). */ 'grey'
    | 'violet';
  activeColor?:
    | 'blue'
    | 'gray'
    | /** @deprecated Use 'gray' instead. British spelling kept for backwards compatibility (DES-25). */ 'grey'
    | 'purple'
    | 'violet';
  size?: 'xsmall' | 'small' | 'medium';
  appearance?: 'rectangle' | 'square' | 'pill';
  includeTooltip?: boolean;
  outerAppearance?: 'default' | 'rounded' | 'pill';
  /** Stretches the control to its container's width and shares it equally between options. */
  fullWidth?: boolean;
  'data-testid'?: string;
};

export const segmentedControlButton = tailwindVariants({
  base: 'flex items-center justify-center rounded-4 font-medium text-14 text-gray-12',
  compoundVariants: [
    // Color variants
    {
      class: 'ads:bg-primary bg-blue-9 text-white hover:ads:bg-hover-primary hover:bg-blue-9',
      color: 'blue',
      state: 'selected',
    },
    {
      class: 'ads:bg-base bg-gray-1 hover:ads:bg-base hover:bg-gray-1',
      color: 'gray',
      state: 'selected',
    },
    {
      class: 'ads:bg-base bg-gray-1 hover:ads:bg-base hover:bg-gray-1',
      color: 'grey',
      state: 'selected',
    },
    {
      class: 'bg-violet-5 text-white hover:bg-violet-5',
      color: 'violet',
      state: 'selected',
    },
    {
      activeColor: 'purple',
      class: 'ads:bg-ads-violet-9 bg-purple-7 ads:text-inverse text-white hover:ads:bg-ads-violet-9 hover:bg-purple-7',
      state: 'selected',
    },
    {
      activeColor: 'violet',
      class: 'ads:bg-ads-violet-9 bg-violet-9 ads:text-inverse text-white hover:ads:bg-ads-violet-9 hover:bg-violet-9',
      state: 'selected',
    },
    {
      activeColor: 'gray',
      class: 'ads:bg-inverse bg-gray-12 ads:text-inverse text-gray-1 hover:ads:bg-inverse hover:bg-gray-12',
      state: 'selected',
    },
    {
      activeColor: 'grey',
      class: 'ads:bg-inverse bg-gray-12 ads:text-inverse text-gray-1 hover:ads:bg-inverse hover:bg-gray-12',
      state: 'selected',
    },
    {
      class: 'bg-transparent ads:text-primary hover:bg-transparent disabled:opacity-50 aria-disabled:opacity-50',
      state: 'unselected',
    },
    // Square appearance variants
    {
      appearance: 'square',
      class: 'px-1.5 py-1.5 text-12',
      size: 'small',
    },
    {
      appearance: 'square',
      class: 'px-2 py-2 text-14',
      size: 'medium',
    },
  ],
  defaultVariants: {
    appearance: 'rectangle',
    color: 'gray',
    fullWidth: false,
    size: 'medium',
    state: 'unselected',
  },
  variants: {
    activeColor: {
      blue: '',
      gray: '',
      /** @deprecated Use 'gray' instead. British spelling kept for backwards compatibility (DES-25). */
      grey: '',
      purple: '',
      violet: '',
    },
    appearance: {
      pill: 'rounded-full',
      rectangle: 'rounded-6',
      square: 'rounded-8',
    },
    color: {
      blue: '',
      gray: '',
      /** @deprecated Use 'gray' instead. British spelling kept for backwards compatibility (DES-25). */
      grey: '',
      violet: '',
    },
    fullWidth: {
      false: '',
      true: 'flex-1',
    },
    size: {
      medium: 'px-4 py-1 text-14',
      small: 'px-2 py-1 text-12',
      // Denser pill for tight rows (e.g. the share-link modal's 32px setting rows).
      xsmall: 'px-1.5 py-0.5 text-12 leading-4',
    },
    state: {
      selected: '',
      unselected: 'bg-transparent',
    },
  },
});

const segmentedControlOuter = tailwindVariants({
  base: 'inline-flex items-center bg-gray-4 p-1',
  compoundVariants: [
    {
      class: 'bg-gray-4',
      outerColor: 'gray',
    },
    {
      class: 'bg-gray-4',
      outerColor: 'grey',
    },
    {
      class: 'bg-violet-4',
      outerColor: 'violet',
    },
  ],
  defaultVariants: {
    fullWidth: false,
    outerColor: 'gray',
  },
  variants: {
    fullWidth: {
      false: '',
      true: 'flex w-full',
    },
    outerAppearance: {
      default: 'rounded-8',
      pill: 'rounded-full',
      rounded: 'rounded-12',
    },
    outerColor: {
      gray: '',
      /** @deprecated Use 'gray' instead. British spelling kept for backwards compatibility (DES-25). */
      grey: '',
      violet: '',
    },
  },
});

export type SegmentedControlButtonVariants = VariantProps<typeof segmentedControlButton>;

export const SegmentedControl = memo(
  ({
    options,
    selectedOption,
    onSelect,
    color = 'gray',
    outerColor = 'gray',
    size = 'medium',
    activeColor,
    appearance = 'rectangle',
    includeTooltip = false,
    outerAppearance = 'default',
    fullWidth = false,
    'data-testid': dataTestId,
  }: SegmentedControlProps) => {
    const isSelected = useCallback(
      (option: SegmentedControlOption) => selectedOption.value === option.value,
      [selectedOption],
    );
    const handleOptionClick = useCallback(
      (event: MouseEvent<HTMLButtonElement>) => {
        const option = options.find(({ value }) => value === event.currentTarget.value);

        if (!option || option.disabled || isSelected(option)) {
          return;
        }

        onSelect(option);
      },
      [isSelected, onSelect, options],
    );

    return (
      <div
        className={segmentedControlOuter({
          fullWidth,
          outerAppearance,
          outerColor,
        })}
        data-testid={dataTestId}
      >
        {options.map((option) => {
          const button = (
            <button
              aria-disabled={includeTooltip && option.disabled ? true : undefined}
              aria-pressed={isSelected(option)}
              className={segmentedControlButton({
                activeColor,
                appearance,
                color: activeColor ? undefined : color,
                fullWidth,
                size,
                state: isSelected(option) ? 'selected' : 'unselected',
              })}
              data-item-id={option.id}
              data-testid={option.testId ?? 'SEGMENTED_CONTROL_BUTTON'}
              disabled={!includeTooltip && option.disabled}
              key={option.value}
              onClick={handleOptionClick}
              type="button"
              value={option.value}
            >
              {option.label}
            </button>
          );

          return includeTooltip ? (
            <Tooltip key={option.value} label={option.tooltip}>
              {button}
            </Tooltip>
          ) : (
            button
          );
        })}
      </div>
    );
  },
);

SegmentedControl.displayName = 'SegmentedControl';
